import { createApp } from 'vue'
import { createPinia } from 'pinia'
import posthog from 'posthog-js'
import App from './App.vue'
import router from './router'
import './assets/main.css'
import Vueform from '@vueform/vueform'
import vueformConfig from '../vueform.config'
import Material from '@primeuix/themes/material';
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import { useAuthStore } from './stores/auth'

if (import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    capture_pageview: false,
  })
}

const app = createApp(App)
const pinia = createPinia()
app.use(pinia)
app.use(Vueform, vueformConfig)

app.use(PrimeVue, {
  theme: {
      preset: Material,
      options: {
          prefix: 'p',
          darkModeSelector: false || 'none',
          cssLayer: false
      }
  }
});
app.use(ToastService)
app.use(router)

// Track auth initialization state to prevent race conditions with router guard
let authInitPromise: Promise<void> | null = null
let authInitComplete = false
let authInitTimedOut = false
let actualAuthPromise: Promise<void> | null = null

// Initialize auth before mounting app
// Wrap in try-catch and timeout so app still mounts if auth initialization fails or hangs
const initializeApp = async () => {
  try {
    const authStore = useAuthStore()
    // Store the actual auth promise separately to track if it's still running
    actualAuthPromise = authStore.initAuth()
    
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Auth initialization timeout')), 5000)
    )
    
    // Race between actual auth and timeout
    authInitPromise = Promise.race([actualAuthPromise, timeoutPromise])
      .then(() => {
        authInitComplete = true
        authInitTimedOut = false
      })
      .catch((err) => {
        // Check if it was a timeout or actual error
        if (err.message?.includes('timeout')) {
          authInitTimedOut = true
          console.warn('Auth initialization timed out, but promise may still be running')
        }
        authInitComplete = true // Mark as complete even on error/timeout
      })
    
    await authInitPromise
  } catch (err) {
    console.error('Auth initialization failed or timed out:', err)
    authInitComplete = true // Mark as complete even on error
    // App will still mount even if auth initialization fails
  }
  
  app.mount('#app')
}

// Export auth initialization promise for router guard to use
if (typeof window !== 'undefined') {
  ;(window as any).__authInitPromise = function() { return authInitPromise ?? null }
  ;(window as any).__authInitComplete = function() { return authInitComplete }
  ;(window as any).__authInitTimedOut = function() { return authInitTimedOut }
  ;(window as any).__actualAuthPromise = function() { return actualAuthPromise ?? null }
}

// eslint-disable-next-line @typescript-eslint/no-floating-promises
initializeApp()